import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../Components/Header";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  Chip,
  Skeleton,
  Stack,
  useTheme,
  alpha,
  Fade,
  Paper,
} from "@mui/material";
import {
  HeartBroken,
  SentimentVeryDissatisfied,
  Visibility,
  DeleteOutline,
  Explore,
} from "@mui/icons-material";
import axios from "axios";
import Swal from "sweetalert2";
import StudentSidebar from "./StudentSdebar";

import DummyCourseImage from "../Images/DummyCourseImage.jpg";

const GOLD = "#D4AF37";

const Wishlist = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [displayedCards, setDisplayedCards] = useState(6);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sidebarExpanded, setSidebarExpanded] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("jwttoken");
    axios
      .get("https://tdtlworld.com/dss-kaas-backend/lms/user/wishlist/", {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        setCourses(response.data || []);
      })
      .catch((error) => {
        console.error("Error fetching wishlist:", error);
        setError("Failed to load your wishlist. Please try again.");
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const removeCourseFromWishlist = (courseId) => {
    Swal.fire({
      title: "Are you sure?",
      text: "This course will be removed from your wishlist.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ff4d4d",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, remove it!",
    }).then((result) => {
      if (result.isConfirmed) {
        const token = localStorage.getItem("jwttoken");
        axios
          .delete(
            `https://tdtlworld.com/dss-kaas-backend/lms/user/wishlist/${courseId}/`,
            {
              headers: { Authorization: `Bearer ${token}` },
            },
          )
          .then(() => {
            setCourses((prevCourses) =>
              prevCourses.filter((cw) => cw.course.course_id !== courseId),
            );
            Swal.fire({
              title: "Removed!",
              text: "The course has been removed from your wishlist.",
              icon: "success",
              confirmButtonColor: GOLD,
            });
          })
          .catch((error) => {
            Swal.fire("Error!", "Could not remove the course.", "error");
          });
      }
    });
  };

  const SkeletonCard = () => (
    <Grid item xs={12} sm={6} md={4}>
      <Card sx={{ borderRadius: "24px", overflow: "hidden" }}>
        <Skeleton variant="rectangular" height={200} />
        <CardContent>
          <Skeleton variant="text" sx={{ fontSize: "1.5rem", mb: 1 }} />
          <Skeleton variant="text" sx={{ mb: 2 }} />
          <Stack direction="row" spacing={1}>
            <Skeleton variant="circular" width={60} height={24} />
            <Skeleton variant="circular" width={60} height={24} />
          </Stack>
        </CardContent>
      </Card>
    </Grid>
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        overflow: "hidden",
        bgcolor: "#fcfcfc",
      }}
    >
      <Header />
      <Box sx={{ display: "flex", flexGrow: 1, overflow: "hidden" }}>
        <StudentSidebar onToggle={setSidebarExpanded} />

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: { xs: 2, md: 4, lg: 6 },
            width: sidebarExpanded ? "calc(100% - 260px)" : "calc(100% - 80px)",
            transition: theme.transitions.create("width", { duration: 300 }),
            height: "calc(100vh - 64px)",
            overflowY: "auto",
          }}
        >
          <Container maxWidth="lg">
            <Box sx={{ mb: 8, textAlign: "center" }}>
              <Typography
                variant="h2"
                fontWeight={900}
                gutterBottom
                sx={{
                  letterSpacing: "-1px",
                  background: `linear-gradient(45deg, #1a1a1a, ${GOLD})`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                My Wishlist
              </Typography>
              <Typography variant="h6" color="text.secondary" fontWeight={400}>
                Saved courses you planned to master.
              </Typography>
            </Box>

            {loading ? (
              <Grid container spacing={4}>
                {Array.from({ length: 3 }).map((_, idx) => (
                  <SkeletonCard key={idx} />
                ))}
              </Grid>
            ) : error ? (
              <Box sx={{ textAlign: "center", py: 10 }}>
                <SentimentVeryDissatisfied
                  sx={{ fontSize: "5rem", color: "error.main", mb: 2 }}
                />
                <Typography variant="h5" fontWeight={700} gutterBottom>
                  Oops! Something went wrong.
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {error}
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={4}>
                {courses.length > 0 ? (
                  courses.slice(0, displayedCards).map(({ course }) => (
                    <Grid item xs={12} sm={6} md={4} key={course.course_id}>
                      <Fade in={true}>
                        <Card
                          sx={{
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                            borderRadius: "24px",
                            border: "1px solid",
                            borderColor: "divider",
                            transition:
                              "0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
                            "&:hover": {
                              transform: "translateY(-10px)",
                              borderColor: GOLD,
                              boxShadow: `0 20px 40px ${alpha(GOLD, 0.1)}`,
                            },
                          }}
                        >
                          <CardMedia
                            component="img"
                            height="200"
                            image={course.cover_img || DummyCourseImage}
                            alt={course.title}
                          />
                          <CardContent sx={{ flexGrow: 1, p: 4 }}>
                            <Typography
                              variant="h6"
                              fontWeight={800}
                              gutterBottom
                              sx={{
                                minHeight: "3.6rem",
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                                lineHeight: 1.2,
                              }}
                            >
                              {course.title}
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                mb: 3,
                                minHeight: "3rem",
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                                lineHeight: 1.6,
                              }}
                            >
                              {course.description ||
                                "No description available for this course."}
                            </Typography>

                            <Stack
                              direction="row"
                              spacing={1}
                              flexWrap="wrap"
                              sx={{ mb: 4 }}
                            >
                              {course.categories?.map((category, index) => (
                                <Chip
                                  key={index}
                                  label={category}
                                  size="small"
                                  sx={{
                                    bgcolor: alpha(GOLD, 0.05),
                                    color: GOLD,
                                    fontWeight: 700,
                                    borderRadius: "6px",
                                  }}
                                />
                              ))}
                            </Stack>

                            <Stack direction="row" spacing={2}>
                              <Button
                                fullWidth
                                variant="contained"
                                startIcon={<Visibility />}
                                onClick={() =>
                                  navigate(
                                    `/db-shiksha/kaas/student/course/${course.course_id}`,
                                  )
                                }
                                sx={{
                                  borderRadius: "50px",
                                  fontWeight: 800,
                                  bgcolor: GOLD,
                                  "&:hover": { bgcolor: alpha(GOLD, 0.8) },
                                }}
                              >
                                View
                              </Button>
                              <Button
                                fullWidth
                                variant="outlined"
                                color="error"
                                startIcon={<DeleteOutline />}
                                onClick={() =>
                                  removeCourseFromWishlist(course.course_id)
                                }
                                sx={{ borderRadius: "50px", fontWeight: 800 }}
                              >
                                Remove
                              </Button>
                            </Stack>
                          </CardContent>
                        </Card>
                      </Fade>
                    </Grid>
                  ))
                ) : (
                  <Grid item xs={12}>
                    <Paper
                      sx={{
                        p: 10,
                        textAlign: "center",
                        borderRadius: "32px",
                        bgcolor: alpha(GOLD, 0.02),
                        border: "2px dashed",
                        borderColor: alpha(GOLD, 0.1),
                      }}
                    >
                      <HeartBroken
                        sx={{
                          fontSize: "5rem",
                          color: alpha(GOLD, 0.2),
                          mb: 2,
                        }}
                      />
                      <Typography variant="h5" fontWeight={800} gutterBottom>
                        Your wishlist is empty
                      </Typography>
                      <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{ mb: 4 }}
                      >
                        Time to explore our catalog and find something
                        interesting!
                      </Typography>
                      <Button
                        variant="contained"
                        size="large"
                        startIcon={<Explore />}
                        onClick={() => navigate("/db-shiksha/kaas/student/courses")}
                        sx={{
                          borderRadius: "50px",
                          px: 6,
                          fontWeight: 800,
                          bgcolor: GOLD,
                          "&:hover": { bgcolor: alpha(GOLD, 0.8) },
                        }}
                      >
                        Explore Courses
                      </Button>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            )}

            {courses.length > displayedCards && (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => setDisplayedCards(displayedCards + 6)}
                  sx={{
                    borderRadius: "50px",
                    px: 6,
                    fontWeight: 800,
                    borderColor: GOLD,
                    color: GOLD,
                    "&:hover": {
                      borderColor: GOLD,
                      bgcolor: alpha(GOLD, 0.05),
                    },
                  }}
                >
                  Load More
                </Button>
              </Box>
            )}
          </Container>
        </Box>
      </Box>
    </Box>
  );
};

export default Wishlist;
